import cx from 'classnames';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { ReadMore } from '../../../design-system/components/read-more/ReadMore';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { DestinationDestinationArticle } from '../../../domains/TravelGuide/Article/DestinationDestinationArticle';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './TitleAndDescription.module.css';

type Props = {
    destination: PickDomain<
        Destination,
        'id' | 'slug' | 'preposition' | 'name' | 'isAvailableForQuoteRequest'
    >;
    destinationArticle: PickDomain<DestinationDestinationArticle, 'contentStart'> | null;
};

export const TitleAndDescription = ({ destination, destinationArticle }: Props) => {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div className={cx('gap-y-16', style.container)}>
            <h1 className={cx('font-secondary-m-bold mb-16', style.title)}>
                {formatMessage(
                    { id: 'page.itinerariesList.titleNew' },
                    {
                        destinationPreposition: destination.preposition,
                        destinationName: destination.name,
                    },
                )}
            </h1>

            {destinationArticle?.contentStart ? (
                <ReadMore
                    buttonProps={{
                        reversed: true,
                    }}
                    lineClamp={3}
                    readMore={formatMessage({ id: 'global.cta.seeMore' })}
                    readLess={formatMessage({ id: 'global.cta.seeLess' })}
                    onToggle={(isOpen) => {
                        if (isOpen) {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.moreDetailsClicked({
                                    ...defaultProperties,
                                    block: 'description',
                                });
                            });
                        }
                    }}
                >
                    <HtmlContent htmlContent={destinationArticle.contentStart} />
                </ReadMore>
            ) : null}

            {destination.isAvailableForQuoteRequest ? (
                <Button
                    obfuscate
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.tripformCreationStartedFromListIti({
                                ...defaultProperties,
                                block: 'cover',
                                destination_id: destination.id,
                                destination_slug: destination.slug,
                            });
                        });
                    }}
                    href={generatePath('trip_form', { destinationSlug: destination.slug })}
                >
                    {formatMessage({ id: 'global.cta.tripForm.default' })}
                </Button>
            ) : null}
        </div>
    );
};
